import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = unauth, obj = auth
    const [hotels, setHotels] = useState([]);
    const [activeHotelId, setActiveHotelId] = useState(
        localStorage.getItem("active_hotel_id") || null,
    );

    const loadUser = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(false);
            return null;
        }
    }, []);

    const loadHotels = useCallback(async () => {
        try {
            const { data } = await api.get("/hotels");
            setHotels(data);
            if (data.length && !activeHotelId) {
                setActiveHotelId(data[0].id);
                localStorage.setItem("active_hotel_id", data[0].id);
            } else if (data.length && activeHotelId && !data.find((h) => h.id === activeHotelId)) {
                setActiveHotelId(data[0].id);
                localStorage.setItem("active_hotel_id", data[0].id);
            }
        } catch (e) {
            console.error("hotels load", e);
        }
    }, [activeHotelId]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    useEffect(() => {
        if (user && user.email) loadHotels();
    }, [user, loadHotels]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) localStorage.setItem("hotel_erp_token", data.access_token);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("hotel_erp_token");
        localStorage.removeItem("active_hotel_id");
        setUser(false);
        setHotels([]);
        setActiveHotelId(null);
    };

    const switchHotel = (id) => {
        setActiveHotelId(id);
        localStorage.setItem("active_hotel_id", id);
    };

    return (
        <AuthContext.Provider value={{
            user, hotels, activeHotelId, login, logout, switchHotel, refreshHotels: loadHotels,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
